import { Dialog, DialogPanel } from "@tremor/react";
import { AlertTriangle, CheckCircle, Info, X, XCircle } from "lucide-react";

// Shared results drawer for the advisory analyzer monitor types
// (security_headers, seo_health, broken_links, image_optimization,
// performance_budget, suspicious_script, platform_fingerprint, carbon_footprint,
// and the platform wrappers). Each renders a common score/findings header plus a
// type-specific detail section read from check.data.

const STATUS_STYLES = {
  up: "bg-green-100 text-green-700 border-green-200",
  degraded: "bg-amber-100 text-amber-700 border-amber-200",
  down: "bg-red-100 text-red-700 border-red-200",
};

const SEVERITY_STYLES = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-gray-50 text-gray-600 border-gray-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
};

const SEVERITY_ICON = {
  high: XCircle,
  medium: AlertTriangle,
  low: Info,
  info: Info,
};

const GRADE_COLOR = (grade) => {
  if (!grade) return "text-gray-500";
  if (grade.startsWith("A")) return "text-green-600";
  if (grade === "B" || grade === "C") return "text-amber-600";
  return "text-red-600";
};

const TYPE_TITLES = {
  security_headers: "Security Headers",
  seo_health: "SEO Health",
  broken_links: "Broken Links",
  image_optimization: "Image Optimization",
  performance_budget: "Performance Budget",
  suspicious_script: "Suspicious Content",
  platform_fingerprint: "Platform Fingerprint",
  carbon_footprint: "Carbon Footprint",
  joomla: "Joomla",
  drupal: "Drupal",
  wix: "Wix",
  webflow: "Webflow",
};

function StatusBadge({ status }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600 border-gray-200",
      ].join(" ")}
    >
      {status ?? "unknown"}
    </span>
  );
}

function fmtBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function Findings({ findings }) {
  if (!findings || findings.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
        <CheckCircle className="h-4 w-4" /> No issues found.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {findings.map((f, i) => {
        const Icon = SEVERITY_ICON[f.severity] ?? Info;
        return (
          <div
            key={i}
            className={[
              "flex items-start gap-2 rounded-lg border p-3 text-sm",
              SEVERITY_STYLES[f.severity] ?? SEVERITY_STYLES.low,
            ].join(" ")}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">{f.message}</p>
              {Array.isArray(f.evidence) && f.evidence.length > 0 && (
                <p className="mt-1 break-all font-mono text-xs opacity-80">
                  {f.evidence.join(", ")}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScoreHeader({ data }) {
  const score = data.score;
  const grade = data.grade;
  if (score == null && grade == null) return null;
  return (
    <div className="flex items-center gap-6 rounded-lg border border-gray-200 bg-white p-4">
      {grade != null && (
        <div className="text-center">
          <div className={`text-4xl font-bold ${GRADE_COLOR(grade)}`}>{grade}</div>
          <div className="text-xs text-gray-400">grade</div>
        </div>
      )}
      {score != null && (
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-800">{score}</div>
          <div className="text-xs text-gray-400">score / 100</div>
        </div>
      )}
    </div>
  );
}

function KV({ rows }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v], i) => (
            <tr key={i} className="border-b border-gray-100 last:border-0">
              <td className="bg-gray-50 px-3 py-2 font-medium text-gray-600">{k}</td>
              <td className="break-all px-3 py-2 text-gray-800">{v ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SecurityHeadersDetail({ data }) {
  const headers = data.headers ?? {};
  const cookies = data.cookies ?? [];
  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Header</th>
              <th className="px-3 py-2">Present</th>
              <th className="px-3 py-2">Recommendation</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(headers).map(([key, h]) => (
              <tr key={key} className="border-b border-gray-100 last:border-0">
                <td className="px-3 py-2 font-mono text-xs">{key}</td>
                <td className="px-3 py-2">
                  {h.present ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">{h.recommendation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cookies.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-gray-500">Cookies</p>
          <div className="space-y-1">
            {cookies.map((c, i) => (
              <div key={i} className="rounded border border-gray-200 px-3 py-2 text-xs">
                <span className="font-mono">{c.name}</span>
                {c.issues.length > 0 ? (
                  <span className="ml-2 text-amber-600">{c.issues.join(", ")}</span>
                ) : (
                  <span className="ml-2 text-green-600">secure</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BrokenLinksDetail({ data }) {
  const broken = data.broken ?? [];
  return (
    <div className="space-y-3">
      <KV
        rows={[
          ["Pages crawled", data.pages_crawled],
          ["Links checked", data.links_checked],
          ["Broken", data.broken_count],
        ]}
      />
      {broken.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Anchor</th>
                <th className="px-3 py-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {broken.map((b, i) => (
                <tr
                  key={i}
                  className="border-b border-gray-100 last:border-0 align-top"
                >
                  <td className="break-all px-3 py-2 font-mono text-xs">
                    {b.target_url}
                  </td>
                  <td className="px-3 py-2 text-xs text-red-600">
                    {b.http_status ?? b.error}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {b.anchor_text || "—"}
                  </td>
                  <td className="break-all px-3 py-2 font-mono text-xs text-gray-400">
                    {b.source_page}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PerformanceBudgetDetail({ data }) {
  const results = data.results ?? [];
  if (results.length === 0)
    return <p className="text-sm text-gray-500">No budgets configured.</p>;
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2">Metric</th>
            <th className="px-3 py-2">Budget</th>
            <th className="px-3 py-2">Actual</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r, i) => (
            <tr key={i} className="border-b border-gray-100 last:border-0">
              <td className="px-3 py-2">{r.label}</td>
              <td className="px-3 py-2 text-gray-600">
                {r.limit}
                {r.unit}
              </td>
              <td className="px-3 py-2 text-gray-800">
                {r.actual == null ? "—" : `${r.actual}${r.unit}`}
              </td>
              <td className="px-3 py-2">
                <span
                  className={
                    r.status === "fail"
                      ? "text-red-600"
                      : r.status === "pass"
                        ? "text-green-600"
                        : "text-gray-400"
                  }
                >
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlatformDetail({ data }) {
  return (
    <KV
      rows={[
        ["Platform", data.detected_platform ?? data.platform],
        ["Confirmed", data.confirmed == null ? undefined : String(data.confirmed)],
        ["Confidence", data.confidence],
        ["Version", data.version],
        ["Generator", data.generator],
        ["Advisory", data.advisory_url],
      ].filter(([, v]) => v !== undefined)}
    />
  );
}

function CarbonDetail({ data }) {
  return (
    <KV
      rows={[
        ["CO₂ per view", data.co2_grams != null ? `${data.co2_grams} g` : "—"],
        ["Rating", data.rating],
        ["Page weight", fmtBytes(data.bytes)],
        ["Requests", data.request_count],
        ["Green host", String(data.green_host ?? false)],
        [
          "Cleaner than",
          data.cleaner_than != null ? `${Math.round(data.cleaner_than * 100)}%` : "—",
        ],
      ]}
    />
  );
}

function ImageDetail({ data }) {
  const totals = data.totals ?? {};
  return (
    <KV
      rows={[
        ["Images", totals.image_count],
        ["Image weight", fmtBytes(totals.image_bytes)],
        [
          "Image share",
          totals.image_share != null ? `${Math.round(totals.image_share * 100)}%` : "—",
        ],
        ["Oversized", totals.oversized_count],
        ["Legacy format", totals.legacy_format_count],
        ["Missing dimensions", totals.missing_dimensions_count],
      ]}
    />
  );
}

function PerfSnapshot({ perf }) {
  if (!perf) return null;
  return (
    <KV
      rows={[
        ["LCP", perf.lcp_ms != null ? `${perf.lcp_ms} ms` : "—"],
        ["CLS", perf.cls],
        ["TTFB", perf.ttfb_ms != null ? `${perf.ttfb_ms} ms` : "—"],
        ["Total weight", fmtBytes(perf.total_weight_bytes)],
        ["Requests", perf.request_count],
      ]}
    />
  );
}

function DetailSection({ type, data }) {
  switch (type) {
    case "security_headers":
      return <SecurityHeadersDetail data={data} />;
    case "broken_links":
      return <BrokenLinksDetail data={data} />;
    case "performance_budget":
      return <PerformanceBudgetDetail data={data} />;
    case "image_optimization":
      return <ImageDetail data={data} />;
    case "carbon_footprint":
      return <CarbonDetail data={data} />;
    case "platform_fingerprint":
      return <PlatformDetail data={data} />;
    case "joomla":
    case "drupal":
    case "wix":
    case "webflow":
      return (
        <div className="space-y-4">
          <PlatformDetail data={data} />
          <PerfSnapshot perf={data.perf} />
        </div>
      );
    default:
      return null;
  }
}

export default function AdvisoryCheckDrawer({ check, type, onClose }) {
  const data = check.data ?? {};
  const findings = data.findings ?? [];
  const title = TYPE_TITLES[type] ?? "Check details";

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-3xl flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <StatusBadge status={check.status} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <p className="text-xs text-gray-400">
            {check.time ? new Date(check.time).toLocaleString() : null}
            {data.final_url ? ` · ${data.final_url}` : null}
          </p>

          <ScoreHeader data={data} />

          {check.error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
              {check.error}
            </div>
          )}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Findings
            </p>
            <Findings findings={findings} />
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Details
            </p>
            <DetailSection type={type} data={data} />
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}

// Monitor types that should render with this generic advisory drawer.
export const ADVISORY_TYPES = new Set([
  "security_headers",
  "seo_health",
  "broken_links",
  "image_optimization",
  "performance_budget",
  "suspicious_script",
  "platform_fingerprint",
  "carbon_footprint",
  "joomla",
  "drupal",
  "wix",
  "webflow",
]);
